import { config } from "@/data/config";

const twitterHandle = config.Socials.Twitter
	? `@${config.Socials.Twitter.split("/").pop()}`
	: undefined;

const sameAs = Object.values(config.Socials).filter(Boolean) as string[];

function mergeSeoKeywords(
	keywords: string | undefined,
	tags: string[] | undefined,
): string | undefined {
	const parts = [
		...(keywords
			? keywords
					.split(",")
					.map((s) => s.trim())
					.filter(Boolean)
			: []),
		...(tags ?? []),
	];
	const unique = [...new Set(parts)];
	return unique.length ? unique.join(", ") : undefined;
}

export const seo = ({
	title,
	description,
	keywords,
	tags,
	canonicalPath,
	image,
	url,
	type = "WebSite",
	author = config.Name,
	datePublished,
	dateModified,
}: {
	title: string;
	description?: string;
	/** Legacy comma-separated keywords; merged with `tags` for articles. */
	keywords?: string;
	/** Post/article tags — merged into meta keywords, Open Graph `article:tag`, and JSON-LD. */
	tags?: string[];
	canonicalPath?: string;
	image?: string;
	url?: string;
	type?: "WebSite" | "Article";
	author?: string;
	datePublished?: string;
	dateModified?: string;
}) => {
	const resolvedUrl = canonicalPath ? `${config.Url}${canonicalPath}` : url;
	const ogType = type === "Article" ? "article" : "website";
	const mergedKeywords =
		type === "Article" ? mergeSeoKeywords(keywords, tags) : keywords;

	const metaTags = [
		{ title },
		{ name: "description", content: description },
		{ name: "keywords", content: mergedKeywords },
		{ name: "author", content: author },
		{ name: "twitter:title", content: title },
		{ name: "twitter:description", content: description },
		...(twitterHandle
			? [
					{ name: "twitter:creator", content: twitterHandle },
					{ name: "twitter:site", content: twitterHandle },
				]
			: []),
		{
			name: "twitter:card",
			content: image ? "summary_large_image" : "summary",
		},
		{ name: "og:type", content: ogType },
		{ name: "og:title", content: title },
		{ name: "og:description", content: description },
		{ name: "og:site_name", content: config.Name },
		{ name: "og:locale", content: "en_US" },
		...(resolvedUrl ? [{ name: "og:url", content: resolvedUrl }] : []),
		...(image
			? [
					{ name: "twitter:image", content: image },
					{ name: "og:image", content: image },
				]
			: []),
		...(type === "Article" && tags?.length
			? tags.map((tag) => ({ name: "article:tag", content: tag }))
			: []),
	];

	// Only emit JSON-LD for Article pages — site-wide WebSite + Person schemas
	// are injected by the root route so they appear on every page without duplication.
	if (type !== "Article") {
		return {
			meta: metaTags,
			scripts: [],
			links: [
				...(resolvedUrl
					? [{ rel: "canonical" as const, href: resolvedUrl }]
					: []),
			],
		};
	}

	const articleSchema = {
		"@context": "https://schema.org",
		"@type": "Article",
		headline: title,
		description,
		image,
		...(mergedKeywords ? { keywords: mergedKeywords } : {}),
		...(resolvedUrl ? { url: resolvedUrl } : {}),
		...(resolvedUrl
			? { mainEntityOfPage: { "@type": "WebPage", "@id": resolvedUrl } }
			: {}),
		author: {
			"@type": "Person",
			name: author,
			url: config.Url,
			sameAs,
		},
		publisher: {
			"@type": "Person",
			name: config.Name,
			url: config.Url,
		},
		datePublished,
		dateModified,
	};

	return {
		meta: metaTags,
		scripts: [
			{
				type: "application/ld+json",
				children: JSON.stringify(articleSchema),
			},
		],
	};
};
